import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExternalLink } from 'lucide-react';
import { backendClient } from '@/lib/api-client';
import { toast } from 'react-hot-toast';
import Loading from '@/components/shared/Loading';
import { formatDate } from '@/utils/date-utils';

interface BundleOutput {
  bundle_output_id: string;
  file_reference_guid: string;
  file_name: string;
  folder_path: string | null;
  status: string;
  created_at: string;
  created_by_name: string | null;
  completed_at: string | null;
}

type GeneratedDocsDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterSlug: string;
  bundleId: string;
};

export const GeneratedDocsDialog = ({ open, onOpenChange, matterSlug, bundleId }: GeneratedDocsDialogProps) => {
  const [outputs, setOutputs] = useState<BundleOutput[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    backendClient
      .get(`/bundle/matter/${matterSlug}/${bundleId}/outputs`)
      .then((res) => setOutputs(res.data))
      .catch((err) => toast.error(err?.response?.data?.detail || 'Failed to load generated documents'))
      .finally(() => setIsLoading(false));
  }, [open, matterSlug, bundleId]);

  const fileUrl = (guid: string) => `/matter/${matterSlug}/f/${guid}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Generated Documents</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loading /></div>
        ) : outputs.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No documents have been generated yet.</p>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 border-b sticky top-0 bg-white">
                <tr>
                  <th className="pb-2 font-medium">File</th>
                  <th className="pb-2 font-medium">Folder</th>
                  <th className="pb-2 font-medium">Created by</th>
                  <th className="pb-2 font-medium">Date</th>
                  <th className="pb-2 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {outputs.map((output) => (
                  <tr key={output.bundle_output_id} className="hover:bg-gray-50">
                    <td className="py-2 pr-3">
                      <a
                        href={fileUrl(output.file_reference_guid)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline truncate block max-w-[200px]"
                        title={output.file_name}
                      >
                        {output.file_name}
                      </a>
                    </td>
                    <td className="py-2 pr-3 text-gray-600 truncate max-w-[150px]" title={output.folder_path ?? ''}>
                      {output.folder_path ?? '—'}
                    </td>
                    <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">
                      {output.created_by_name ?? 'Unknown'}
                    </td>
                    <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">
                      {formatDate(output.created_at, 'd MMM yyyy, h:mm a')}
                    </td>
                    <td className="py-2">
                      <a
                        href={fileUrl(output.file_reference_guid)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
